import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {Grid, GridItem, Show, Text, VStack} from 'platform/foundation';
import {array, object, boolean} from 'yup';

import {useGetTenantV2Query} from '@dms/api/base';
import i18n from '@dms/i18n';
import {useCurrencies} from '@dms/shared';

import {yupString} from 'shared/web';

import {ALL_BANK_ACCOUNT_DOMAINS} from '../constants/ALL_BANK_ACCOUNT_DOMAINS';
import {useBankAccountDomainOptions} from '../hooks/useBankAccountDomainOptions';
import type {BankAccountDomainSelectValue} from '../types/BankAccountDomainSelectValue';
import type {BillingInformationDetailFormValues} from '../types/BillingInformationDetailFormValues';
import {getRealBankAccountDomains} from '../utils/getRealBankAccountDomains';
import {normalizeBankAccountDomainSelection} from '../utils/normalizeBankAccountDomainSelection';
import {toBankAccountDomainSelectValue} from '../utils/toBankAccountDomainSelectValue';

type FormValues = BillingInformationDetailFormValues['bankAccounts'][number];

// The persisted shape stores `domains` as `BankAccountDomain[]`. Inside the form the
// selector also offers the FE-only "All" sentinel, so the form value widens `domains`.
type BankAccountFormValues = Omit<FormValues, 'domains'> & {
  domains: BankAccountDomainSelectValue[];
};

interface DeputyPersonFormProps {
  handleSubmit: (values: FormValues) => void;
  defaultValues?: FormValues;
}

export function BankAccountForm(props: DeputyPersonFormProps) {
  const {data: tenant} = useGetTenantV2Query();
  const {currencyOptions} = useCurrencies();
  const {domainOptions, isDomainAssignmentEnabled} = useBankAccountDomainOptions();

  const handleSubmit: FormSubmitHandler<BankAccountFormValues> = async (values) => {
    const submitValues: FormValues = {
      ...values,
      domains: getRealBankAccountDomains(values.domains),
    };

    await props.handleSubmit(submitValues);
    closeCurrentDialog();
  };

  const defaultValues = {
    ...props.defaultValues,
    currency: props.defaultValues?.currency ?? tenant?.currency,
    domains: toBankAccountDomainSelectValue(props.defaultValues?.domains),
  };

  // "All" is the FE-only representation of an unassigned account, so it sits first
  // and is kept mutually exclusive with the real domains in `handleDomainsChange`.
  const domainSelectOptions = [
    {label: i18n.t('entity.bank.labels.allDomains'), value: ALL_BANK_ACCOUNT_DOMAINS},
    ...domainOptions,
  ];

  return (
    <Form<BankAccountFormValues>
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      schema={schema}
    >
      {(control, formApi) => {
        const handleDomainsChange = () => {
          formApi.setValue(
            'domains',
            normalizeBankAccountDomainSelection(formApi.getValues('domains')),
            {shouldDirty: true}
          );
        };

        return (
          <VStack spacing={5}>
            <Grid columns={4}>
              <GridItem span={4}>
                <FormField
                  control={control}
                  name="accountName"
                  type="text"
                  isRequired
                  label={i18n.t('entity.bank.labels.accountName')}
                />
              </GridItem>
              <GridItem span={2}>
                <FormField
                  control={control}
                  name="accountNumber"
                  type="text"
                  isRequired
                  label={i18n.t('entity.bank.labels.accountNumber')}
                />
              </GridItem>
              <GridItem span={2}>
                <FormField
                  control={control}
                  name="bankName"
                  isRequired
                  type="text"
                  label={i18n.t('entity.bank.labels.name')}
                />
              </GridItem>
              <GridItem span={2}>
                <FormField
                  control={control}
                  name="iban"
                  type="text"
                  label={i18n.t('entity.bank.labels.iban')}
                />
              </GridItem>
              <FormField
                control={control}
                name="swift"
                type="text"
                label={i18n.t('entity.bank.labels.swift')}
              />

              <FormField
                control={control}
                name="currency"
                isNotClearable
                type="choice"
                label={i18n.t('entity.bank.labels.currency')}
                options={currencyOptions}
              />
              <Show when={isDomainAssignmentEnabled}>
                <GridItem span={4}>
                  <VStack spacing={1}>
                    <FormField
                      control={control}
                      name="domains"
                      type="multiChoice"
                      isNotClearable
                      label={i18n.t('entity.bank.labels.domains')}
                      options={domainSelectOptions}
                      onChange={handleDomainsChange}
                    />
                    <Text size="xSmall" color="tertiary">
                      {i18n.t('entity.bank.labels.domainsHelper')}
                    </Text>
                  </VStack>
                </GridItem>
              </Show>
              <GridItem span={4}>
                <FormField
                  control={control}
                  name="isPrimary"
                  type="checkbox"
                  label={i18n.t('entity.bank.labels.primaryAccount')}
                />
              </GridItem>
            </Grid>

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                onClick={closeCurrentDialog}
                variant="secondary"
              />
              <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}

const schema = object({
  accountName: yupString.required(),
  accountNumber: yupString.required(),
  bankName: yupString.required(),
  iban: yupString.default(null),
  swift: yupString.default(null),
  currency: yupString.default(null),
  isPrimary: boolean().default(false),
  isExpanded: boolean().default(false),
  domains: array().of(yupString).default([]),
});
